import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import {
  Subject,
  takeUntil,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  take,
  Observable,
} from 'rxjs';

import { LostSaleService } from '../../../lost-sale/lost-sale.service';
import { LostSaleV2StateService } from '../../services/lost-sale-v2-state.service';
import { LostSaleItem, LostSaleQueryParams } from '../../models/lost-sale.interfaces';
import { UserRouteDisplayComponent } from 'app/shared/components/user-route-display/user-route-display.component';
import { UnitMeasureDisplayComponent } from 'app/shared/components/unit-measure-display/unit-measure-display.component';
import { DocumentNavigationComponent } from 'app/common/components/document-navigation/document-navigation.component';
import { DocumentTypeEnum } from 'app/common/enums/document-types.enum';

@Component({
  selector: 'app-lost-sale-commercial',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    UserRouteDisplayComponent,
    UnitMeasureDisplayComponent,
    DocumentNavigationComponent,
  ],
  templateUrl: './lost-sale-commercial.component.html',
  styleUrls: ['./lost-sale-commercial.component.scss'],
})
export class LostSaleCommercialComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  private destroy$ = new Subject<void>();
  private hasInitialLoad = false;

  displayedColumns: string[] = [
    'trip',
    'routeUser',
    'date',
    'serials',
    'customer',
    'salesOffice',
    'salesGroup',
    'salesZone',
    'material',
    'materialGroup',
    'desiredQuantity',
    'soldQuantity',
    'stockAtCreation',
    'confirmedQuantity',
    'deliveredQuantity',
    'deliveredQuantityERP',
    'invoicedQuantity',
    'commercialLostSale',
    'logisticLostSale',
    'realLostSale',
    'lostSalePercentage',
  ];

  dataSource = new MatTableDataSource<LostSaleItem>([]);
  isLoading$: Observable<boolean>;
  totalItems = 0;
  UMPActive: boolean = true;
  pageSize = 100;
  pageIndex = 0;
  pageSizeOptions = [25, 50, 100, 200, 500];

  DocumentTypeEnum = DocumentTypeEnum;

  constructor(
    private stateService: LostSaleV2StateService,
    private lostSaleService: LostSaleService,
    private snackBar: MatSnackBar,
    private router: Router
  ) {
    this.isLoading$ = this.stateService.loading$;
  }

  ngOnInit(): void {
    const currentPagination = this.stateService.getCurrentCommercialPagination();
    this.pageIndex = currentPagination.pageIndex;
    this.pageSize = currentPagination.pageSize;

    this.setupTableConfiguration();
    this.subscribeToStateChanges();
  }

  private setupTableConfiguration(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  private subscribeToStateChanges(): void {
    this.stateService.queryParamsChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.loadData();
      this.hasInitialLoad = true;
    });

    this.stateService.commercialPage$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(
          (prev, curr) => prev.pageIndex === curr.pageIndex && prev.pageSize === curr.pageSize
        )
      )
      .subscribe(pagination => {
        this.pageIndex = pagination.pageIndex;
        this.pageSize = pagination.pageSize;

        if (this.paginator) {
          this.paginator.pageIndex = pagination.pageIndex;
          this.paginator.pageSize = pagination.pageSize;
        }
      });

    this.stateService.commercialSort$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(
          (prev, curr) => prev.active === curr.active && prev.direction === curr.direction
        )
      )
      .subscribe(() => {
        if (this.hasInitialLoad) {
          this.stateService.loading$.pipe(take(1)).subscribe(loading => {
            if (!loading) {
              this.loadData();
            }
          });
        }
      });
  }

  private loadData(): void {
    if (this.stateService.loadingState) {
      return;
    }

    this.stateService.setLoading(true);

    const params: LostSaleQueryParams = {
      filters: this.stateService.getCurrentFilters(),
      search: this.stateService.getCurrentSearchTerm(),
      page: this.pageIndex + 1,
      pageSize: this.pageSize,
      orderBy: this.stateService.getCurrentCommercialSort().active,
      orderDirection:
        this.stateService.getCurrentCommercialSort().direction === 'desc' ? 'DESC' : 'ASC',
    };

    this.lostSaleService.findLostSaleV2(params).subscribe({
      next: response => {
        const actualData = response.data[0].data;
        const actualTotalCount = response.data[0].pageInfo?.[0]?.total;
        const items = this.transformResponseData(actualData);

        this.dataSource.data = items;
        this.totalItems = actualTotalCount;

        this.stateService.setCommercialData(items, actualTotalCount);
        this.stateService.setLoading(false);
      },
      error: error => {
        this.handleError(error);
        this.stateService.setLoading(false);
      },
    });
  }

  private transformResponseData(data): LostSaleItem[] {
    return data.map(item => {
      // Use backend calculated values directly - NO frontend calculations needed
      const desiredVsSold = {
        umb: (item.baseQuantitySuggested || 0) - (item.baseQuantity || 0),
        ump: (item.pararellQuantitySuggested || 0) - (item.pararellQuantity || 0),
        umv: (item.soldQuantitySuggested || 0) - (item.soldQuantity || 0),
        percentage: this.calculatePercentage(item.soldQuantitySuggested, item.soldQuantity),
      };

      const soldVsConfirmed = {
        umb: (item.baseQuantity || 0) - (item.stockConfirmedBase || 0),
        ump: (item.pararellQuantity || 0) - (item.stockConfirmedPararell || 0),
        umv: (item.soldQuantity || 0) - (item.stockConfirmedERP || 0),
        percentage: this.calculatePercentage(item.soldQuantity, item.stockConfirmedERP),
      };

      // VP Logística: Use backend calculated values
      const logisticLostSale = {
        umb: item.baseLogisticLostSale || 0,
        ump: item.pararellLogisticLostSale || 0,
        umv: (item.soldQuantity || 0) - (item.deliveredQuantity || 0),
        percentage: this.calculatePercentage(item.soldQuantity, item.deliveredQuantity),
      };

      // VP Real: Use backend calculated values
      const realLostSale = {
        umb: item.baseRealLostSale || 0,
        ump: item.pararellRealLostSale || 0,
        umv: (item.soldQuantity || 0) - (item.invoiceQuantity || 0),
        percentage: this.calculatePercentage(item.soldQuantity, item.invoiceQuantity),
      };

      // Map backend structure to LostSaleItem interface
      return {
        // Required PrcOrder properties
        id: item._id || item.id || '',
        documentNumber: item.serials || item.documentNumber || '',
        customerId: parseInt(item.shipTo?.id || item.customerId || '0', 10),
        customerName: item.shipToName || item.customerName || '',
        materialId: item.material?._id || item.materialId || '',
        materialName: item.material?.name || item.materialName || '',
        materialGroupId: item.material?.materialGroups?.[0]?._id || item.materialGroupId || '',
        subRouteId: parseInt(item.subRoute?.id || item.subRouteId || '0', 10),
        subRouteName: item.subRoute?.name || item.subRouteName || '',
        userId: item.userId || item.user || '',
        userName: item.user || item.userName || '',
        tripId: item.trip?.id,
        createdAt: new Date(item.createdAt || item.date || new Date()),
        updatedAt: new Date(item.updatedAt || item.date || new Date()),
        companyId: parseInt(item.companyId || '0', 10),
        salesOfficeId: parseInt(item.salesOfficeId || '0', 10),
        warehouseId: parseInt(item.warehouseId || '0', 10),

        // PrcOrderQuantities properties - CORRECTED MAPPING based on API response
        soldOriginalQuantity: item.baseQuantitySuggested || 0, // UMB deseada
        parallelOriginalQuantity: item.pararellQuantitySuggested || 0, // UMP deseada
        soldQuantitySuggested: item.soldQuantitySuggested || 0, // UMV deseada
        availableQuantity: item.availableQuantity || 0,
        availableBaseQuantity: item.availableBaseQuantity || 0,
        availableParallelQuantity: item.availableParallelQuantity || 0,
        soldQuantity: item.soldQuantity || 0, // UMV vendida
        baseQuantity: item.baseQuantity || 0, // UMB vendida
        parallelQuantity: item.pararellQuantity || 0, // UMP vendida
        stockConfirmedBase: item.stockConfirmedBase || 0, // UMB confirmada
        stockConfirmedParallel: item.stockConfirmedPararell || 0, // UMP confirmada
        stockConfirmedUnitOfMeasure: item.stockConfirmedERP || 0, // UMV confirmada
        deliveredQuantity: item.deliveredQuantity || 0, // UMV entregada
        deliveredBaseQuantity: item.deliveredBaseQuantity || 0, // UMB entregada
        deliveredParallelQuantity: item.deliveredPararellQuantity || 0, // UMP entregada
        baseQuantityReceivedByERP: item.baseQuantityReceivedByERP || 0, // UMB entregada ERP
        parallelQuantityReceivedByERP: item.parallelQuantityReceivedByERP || 0, // UMP entregada ERP
        // NEW: Invoiced quantities for VP Real calculations
        invoicedQuantity: item.invoiceQuantity || 0, // UMV facturada
        invoicedBaseQuantity: item.invoiceBaseQuantity || 0, // UMB facturada
        invoicedParallelQuantity: item.invoicePararellQuantity || 0, // UMP facturada
        relatedDocuments: item.relatedDocuments || [],

        // Backend calculated VP values - store for helper methods
        backendCalculatedVP: {
          commercialLostSaleUMB: item.baseLostSale || 0,
          commercialLostSaleUMP: item.pararellLostSale || 0,
          logisticLostSaleUMB: item.baseLogisticLostSale || 0,
          logisticLostSaleUMP: item.pararellLogisticLostSale || 0,
          realLostSaleUMB: item.baseRealLostSale || 0,
          realLostSaleUMP: item.pararellRealLostSale || 0,
        },

        // LostSaleItem specific properties
        calculations: {
          commercialLostSale: {
            desiredVsSold,
            soldVsConfirmed,
          },
          logisticLostSale,
          realLostSale,
        },

        // Units of measure
        unitOfMeasure: {
          base: {
            id: item.baseUnitOfMeasure?._id || '',
            name: item.baseUnitOfMeasure?.name || '',
            code: item.baseUnitOfMeasure?.code || '',
          },
          parallel: {
            id: item.pararellUnitOfMeasure?._id || '',
            name: item.pararellUnitOfMeasure?.name || '',
            code: item.pararellUnitOfMeasure?.code || '',
          },
          sale: {
            id: item.soldUnitOfMeasure?._id || '',
            name: item.soldUnitOfMeasure?.name || '',
            code: item.soldUnitOfMeasure?.code || '',
          },
        },

        // Trip info
        trip: item.trip
          ? {
              id: item.trip.id,
              code: item.trip.externalKeys?.code || '',
              status: 'active', // Default status
              date: new Date(item.trip.dateHourStart || item.date),
            }
          : undefined,

        // Material info
        material: {
          id: item.material?._id || '',
          name: item.material?.name || '',
          code: item.material?.code || '',
          groupId: item.material?.materialGroups?.[0]?._id || '',
          groupName: item.material?.materialGroups?.[0]?.name || '',
        },

        // Customer info
        customer: {
          id: parseInt(item.shipTo?.id || item.customerId || '0', 10),
          name: item.shipToName || '',
          code: item.shipToExternalKeyCode || '',
          address: item.shipToAddress || '',
          zone: item.shipToZone || '',
        },

        // Route info
        route: {
          id: parseInt(item.subRoute?.id || '0', 10),
          name: item.subRoute?.name || '',
          code: item.subRoute?.externalKeys?.code || '',
        },

        // Sub route info
        subRoute: item.subRoute
          ? {
              id: parseInt(item.subRoute.id || '0', 10),
              name: item.subRoute.name,
              code: item.subRoute.externalKeys?.code || '',
              responsibleCode: item.subRoute.responsibleCode,
              responsibleName: item.subRoute.responsibleName,
            }
          : undefined,

        // User info
        user: item?.user,

        // Missing fields from original report
        serials: item.serials || item.documentNumber || '',
        stockAtCreation: item.stockAtCreation || 0,
        stockAtCreationParallel: item.stockAtCreationParallel || 0,

        // API compatibility fields
        customerCode: item.shipToExternalKeyCode,
        materialCode: item.material?.code,
        materialGroupName: item.material?.materialGroups?.[0]?.name,

        // FS-37: Oficina de Venta, Grupo de Vendedor y Zona de Venta
        salesOfficeName: item.salesOffice?.name || '',
        salesOfficeCode: item.salesOffice?.externalKeys?.code || '',
        salesGroup: item.soldToDChainUserFields?.BP_SALES_GROUP || '',
        salesZone: item.soldToDChainUserFields?.BP_SALES_ZONE || '',
      };
    });
  }

  private calculatePercentage(original: number, current: number): number {
    if (!original || original === 0) return 0;
    const lostAmount = original - (current || 0);
    return (lostAmount / original) * 100;
  }

  private handleError(error: any): void {
    const message = error.error?.message || 'Error al cargar datos de venta perdida comercial';
    this.snackBar.open(message, 'Cerrar', {
      duration: 5000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: ['error-snackbar'],
    });
  }

  // Table event handlers
  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.stateService.setCommercialPage(event.pageIndex, event.pageSize);
  }

  onSortChange(sort: Sort): void {
    this.stateService.setCommercialSort(sort.active, sort.direction as 'asc' | 'desc' | '');
  }

  // Navigation
  goToTrip(element: LostSaleItem): void {
    if (element.tripId) {
      const url = this.router.createUrlTree(['/monitor/principal-screen'], {
        queryParams: { tripId: element.tripId },
      });
      window.open(this.router.serializeUrl(url), '_blank');
    }
  }

  hasTrip(element: LostSaleItem): boolean {
    return !!element.tripId && element.tripId > 0;
  }

  // Helper methods for template - USE BACKEND CALCULATED VALUES
  getCommercialLostSaleUMB(element: LostSaleItem): number {
    return (element as any).backendCalculatedVP?.commercialLostSaleUMB || 0;
  }

  getCommercialLostSaleUMP(element: LostSaleItem): number {
    return (element as any).backendCalculatedVP?.commercialLostSaleUMP || 0;
  }

  getLogisticLostSaleUMB(element: LostSaleItem): number {
    return (element as any).backendCalculatedVP?.logisticLostSaleUMB || 0;
  }

  getLogisticLostSaleUMP(element: LostSaleItem): number {
    return (element as any).backendCalculatedVP?.logisticLostSaleUMP || 0;
  }

  getRealLostSaleUMB(element: LostSaleItem): number {
    return (element as any).backendCalculatedVP?.realLostSaleUMB || 0;
  }

  getRealLostSaleUMP(element: LostSaleItem): number {
    return (element as any).backendCalculatedVP?.realLostSaleUMP || 0;
  }

  getLostSalePercentage(element: LostSaleItem): number {
    // Calculate percentage based on VP Comercial vs original quantity
    const vpCommercialUMB = this.getCommercialLostSaleUMB(element);
    const originalUMB = element.soldOriginalQuantity || 0;
    return originalUMB > 0 ? (vpCommercialUMB / originalUMB) * 100 : 0;
  }

  formatNumber(value: number | undefined | null): string {
    if (value === null || value === undefined) return '0';
    return value.toLocaleString('es-HN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  formatPercentage(value: number | undefined | null): string {
    if (value === null || value === undefined) return '0%';
    return value.toFixed(2) + '%';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
